<style scoped lang="scss">
  label {
    @apply text-primary-500 opacity-80 transition-all text-12 md:text-14 focus:text-gray-200;
  }

  input {
    @apply h-[48px] px-4 text-16 font-semibold border bg-none text-primary-500 text-opacity-70 focus:text-gray-200 border-primary-500 border-opacity-60 rounded-[6px] focus:border-gray-200 transition-colors;

    &::placeholder {
      @apply text-primary-500 text-opacity-50;
    }
  }

  .no-buttons {
    .subtract,
    .add {
      display: none;
    }
  }
</style>

<template>
  <ValidationProvider
    ref="provider"
    mode="eager"
    tag="div"
    v-slot="{ classes, errors }"
    :class="{ 'opacity-75 cursor-not-allowed': disabled }"
    :vid="name"
    :name="validateAs"
    :rules="rules"
    :bails="false"
  >
    <div class="flex flex-row items-center justify-between gap-4">
      <label v-if="label" :for="`id_${name}`" :class="[classes]" class="block w-full">
        {{ label }}
        <span v-if="(rules && typeof rules === 'string' && rules.includes('required')) || (typeof rules === 'object' && rules.required)">*</span>
      </label>

      <div class="flex flex-row flex-none gap-4 select-none">
        <div
          class="subtract bg-gray-200 h-[32px] w-[32px] rounded-full flex items-center justify-center cursor-pointer hover:scale-90 transition-transform"
          @click.prevent="handleMin()"
        >
          <IconMinus class="w-[8px] stroke-2 stroke-black"></IconMinus>
        </div>
        <input
          autocomplete="false"
          ref="input"
          min="0"
          type="number"
          v-model="localValue"
          v-on="{ ...$listeners, input: handleInput }"
          :class="[
            classes,
            'w-full focus:outline-none flex items-center transition ease-in-out',
            {
              '!border-gray-300': disabled,
              '!border-red': errors[0],
            },
          ]"
          :id="`id_${name}`"
          :placeholder="placeholder"
          :aria-describedby="`description_${name}`"
        />
        <div class="add bg-gray-200 h-[32px] w-[32px] rounded-full flex items-center justify-center cursor-pointer hover:scale-90 transition-transform" @click.prevent="handlePlus()">
          <IconPlus class="h-[8px] stroke-2 stroke-black"></IconPlus>
        </div>
      </div>
    </div>

    <span v-if="showError && errors[0]" class="text-red text-12 block mt-4">{{ errors[0] }}</span>
  </ValidationProvider>
</template>

<script lang="ts">
  import { Field as ValidationProvider } from "vee-validate";
  import { Component, Vue, Prop, Emit, Watch, Ref } from "vue-facing-decorator";
  import { icons } from "@/assets/svg";

  @Component({
    components: {
      ValidationProvider,
      IconMinus: icons.MinusIcon,
      IconPlus: icons.PlusIcon,
    },
  })
  export default class FormNumber extends Vue {
    @Prop() name?: string;

    @Prop() value?: number;

    @Prop() placeholder?: string;

    @Prop() rules?: string | object;

    @Prop() label?: string;

    @Prop({ default: true }) showError?: boolean;

    @Prop({ default: false }) disabled!: boolean;

    @Ref() readonly provider!: InstanceType<typeof ValidationProvider>;

    localValue = 0;

    handleMin(): void {
      if (this.localValue === 0) {
        return;
      }
      this.localValue -= 1;
      this.handleInput();
    }

    handlePlus(): void {
      this.localValue++;
      this.handleInput();
    }

    private get validateAs(): string {
      if (this.label) {
        return this.label;
      }

      if (this.placeholder) {
        return this.placeholder;
      }

      if (this.name) {
        return this.name;
      }

      return `number field`;
    }

    @Emit("input")
    private handleInput(): number {
      return this.localValue;
    }

    @Watch("value", { immediate: true })
    private valueChanged(newValue: number) {
      this.localValue = newValue ?? 0;
    }
  }
</script>
